import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import type { CardData, CreatureData } from '../types'
import { Card } from './Card'
import { TurnTimer } from './TurnTimer'

interface CreatureSlotProps {
  creature: CreatureData | null
  index: number
  onClick?: () => void
  onDragOver?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent) => void
  selected?: boolean
  canAttack?: boolean
  isOpponent?: boolean
}

function CreatureSlot({
  creature,
  index,
  onClick,
  onDragOver,
  onDrop,
  selected,
  canAttack,
  isOpponent = false,
}: CreatureSlotProps) {
  const [prevHealth, setPrevHealth] = useState(creature?.health || 0)
  const [damageFloat, setDamageFloat] = useState<number | null>(null)
  const [isShaking, setIsShaking] = useState(false)
  const [isDead, setIsDead] = useState(false)
  const [dyingCreature, setDyingCreature] = useState<CreatureData | null>(null)

  useEffect(() => {
    // If we have a live creature, sync it
    if (creature) {
      setDyingCreature(null)
      setIsDead(false)
      if (creature.health < prevHealth) {
        const damage = prevHealth - creature.health
        setDamageFloat(damage)
        setIsShaking(true)
        setTimeout(() => {
          setIsShaking(false)
          setDamageFloat(null)
        }, 800)
      }
    }
    // If creature is gone but we had one, it might be dead
    else if (!creature && prevHealth > 0 && !isDead) {
      // Logic handled by the next effect monitoring lastKnownCreature
    }
    setPrevHealth(creature?.health || 0)
  }, [creature?.health, prevHealth])

  // Track previous creature for death animation
  const [lastKnownCreature, setLastKnownCreature] = useState<CreatureData | null>(creature)

  useEffect(() => {
    if (creature) {
      setLastKnownCreature(creature)
      if (isDead) setIsDead(false) // Reset if slot is reused
    } else if (lastKnownCreature && !isDead) {
      // Creature removed. Assume death for now.
      setIsDead(true)
      setDyingCreature(lastKnownCreature)

      const timer = setTimeout(() => {
        setDyingCreature(null)
        setIsDead(false)
        setLastKnownCreature(null)
      }, 600) // 0.6s for crumble animation
      return () => clearTimeout(timer)
    }
  }, [creature, lastKnownCreature, isDead])

  const displayCreature = creature || dyingCreature

  const [isOver, setIsOver] = useState(false)

  const handleDragOverKey = (e: React.DragEvent) => {
    e.preventDefault()
    setIsOver(true)
    onDragOver?.(e)
  }

  const handleDragLeave = () => {
    setIsOver(false)
  }

  const handleDropKey = (e: React.DragEvent) => {
    e.preventDefault()
    setIsOver(false)
    onDrop?.(e)
  }

  if (!displayCreature) {
    return null
  }

  return (
    <button
      type="button"
      className={cn(
        'relative transition-all duration-300 transform group/slot',
        selected && 'ring-2 ring-primary ring-offset-2 ring-offset-black scale-105 z-20',
        !isOpponent &&
        canAttack &&
        'ring-1 ring-accent ring-offset-1 ring-offset-black shadow-[0_0_15px_rgba(78,205,196,0.3)]',
        isOpponent && selected && 'ring-destructive',
        isDead && 'animate-crumble pointer-events-none',
        isOver &&
        'ring-4 ring-yellow-400 ring-offset-2 ring-offset-black scale-105 z-30 shadow-[0_0_25px_rgba(250,204,21,0.5)]',
      )}
      onClick={onClick}
      onDragOver={handleDragOverKey}
      onDragLeave={handleDragLeave}
      onDrop={handleDropKey}
    >
      <div
        className={cn(
          'w-[170px] h-[230px] relative transition-transform',
          isShaking && 'animate-shake',
        )}
      >
        <Card
          hideCost
          card={{
            ...displayCreature.card,
            power: displayCreature.currentPower,
            health: displayCreature.currentHealth,
            keywords: displayCreature.keywords,
          }}
        />

        {/* Hardware Status Column (Equipped Buffs) */}
        {displayCreature.equippedItems && displayCreature.equippedItems.length > 0 && (
          <div className="absolute top-0 right-0 h-full w-1 flex flex-col gap-[2px] z-30 py-2">
            {displayCreature.equippedItems.map((_, idx) => (
              <div
                key={idx}
                className="flex-1 bg-white shadow-[0_0_5px_rgba(255,255,255,0.8)] animate-pulse"
                title="Active Module"
              />
            ))}
            {/* Empty Slots for aesthetic consistency */}
            {Array.from({ length: Math.max(0, 5 - displayCreature.equippedItems.length) }).map((_, idx) => (
              <div
                key={`empty-${idx}`}
                className="flex-1 bg-white/5"
              />
            ))}
          </div>
        )}

        {/* Overdrive Aura for Buffed/High Power Creatures */}
        {displayCreature.currentPower > displayCreature.card.power! && (
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-white/10 to-transparent pointer-events-none z-10 animate-pulse" />
        )}

        {damageFloat && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none animate-float-up">
            <span className="text-4xl font-black text-red-500 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] tracking-tighter">
              -{damageFloat}
            </span>
          </div>
        )}
      </div>
    </button>
  )
}

interface GameBoardProps {
  playerCreatures?: (CreatureData | null)[]
  opponentCreatures?: (CreatureData | null)[]
  onPlayerCreatureClick?: (creature: CreatureData) => void
  onOpponentCreatureClick?: (creature: CreatureData) => void
  selectedCreatureId?: string | null
  dragOverSlot: number | null
  handleDragOver: (e: React.DragEvent, position: number) => void
  handleDragLeave: () => void
  handleDrop: () => void
  onDropOnCreature?: (creatureId: string, cardId: string) => void
  onFaceClick?: () => void
  turnTimeRemaining: number
  turnTimerConfig: number
  isMyTurn: boolean
  selectedTheme?: 'FANTASY' | 'NOIR' | 'SUNSET'
}

export function GameBoard({
  playerCreatures = [],
  opponentCreatures = [],
  onPlayerCreatureClick,
  onOpponentCreatureClick,
  selectedCreatureId = null,
  dragOverSlot,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  onDropOnCreature,
  onFaceClick,
  turnTimeRemaining,
  turnTimerConfig,
  isMyTurn,
  selectedTheme,
}: GameBoardProps) {
  const timerProgress = (turnTimeRemaining / turnTimerConfig) * 100
  const isLowTime = turnTimeRemaining <= 10
  return (
    <div className="flex flex-col flex-1 overflow-y-auto rounded-none relative z-10 font-interface">
      {/* Target Face Overlay */}
      <button
        type="button"
        className={cn(
          'h-12 shrink-0 flex items-center justify-center transition-all absolute top-4 left-1/2 -translate-x-1/2 z-50',
          selectedCreatureId ? 'opacity-100 animate-pulse scale-100' : 'opacity-0 pointer-events-none scale-90',
        )}
        onClick={() => selectedCreatureId && onFaceClick?.()}
      >
        <div className="w-16 h-16 border-2 border-white/10 flex items-center justify-center rotate-45 group">
          <div className="w-12 h-12 border border-white/20 flex items-center justify-center">
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full shadow-[0_0_10px_rgba(239,68,68,1)]" />
          </div>
        </div>
      </button>

      {/* Unified Battlefield */}
      <div className="flex-1 flex flex-col justify-center items-center relative overflow-hidden">
        {/* Opponent Lane */}
        <div className="w-full flex justify-center gap-4 py-4 relative flex-1 items-center px-6">
          {opponentCreatures.map((creature, i) => (
            <CreatureSlot
              key={`opp-slot-${i}`}
              index={i}
              creature={creature}
              isOpponent
              onClick={() => creature && onOpponentCreatureClick?.(creature)}
              onDragOver={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onDrop={(e) => {
                const cardId = e.dataTransfer.getData('cardId')
                if (cardId && creature) onDropOnCreature?.(creature.id, cardId)
              }}
            />
          ))}
        </div>

        {/* Central Timer Divider - Absolute Edge to Edge */}
        <div className="w-full relative z-20">
          <TurnTimer
            progress={timerProgress}
            isLowTime={isLowTime}
            isMyTurn={isMyTurn}
          />
        </div>

        {/* Player Lane */}
        <div
          className={cn(
            'w-full flex justify-center gap-4 py-4 transition-all duration-300 flex-1 items-center rounded-lg',
            dragOverSlot !== null
              ? 'bg-white/[0.02] ring-1 ring-white/10'
              : '',
          )}
          onDragOver={(e) => handleDragOver(e, 0)}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {playerCreatures.map((creature, i) => (
            <CreatureSlot
              key={`player-slot-${i}`}
              index={i}
              creature={creature}
              canAttack={creature?.canAttack}
              selected={selectedCreatureId === creature?.id}
              onClick={() => creature && onPlayerCreatureClick?.(creature)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={(e) => {
                const cardId = e.dataTransfer.getData('cardId') || (window as any)._draggedCardId
                if (cardId && creature) {
                  onDropOnCreature?.(creature.id, cardId)
                } else {
                  handleDrop()
                }
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
